import {afterNextRender, Component, inject, Injector, OnDestroy, OnInit, Renderer2} from "@angular/core";
import {Action, Store} from "@ngrx/store";
import {MatSnackBar} from "@angular/material/snack-bar";
import {
    FundDetailActions,
    FundReportActions,
    FundValuationActions,
    UndrawnValuationActions
} from "../../store/fund.actions";
import {
    selectFundForms,
    selectFundIsArchived,
    selectFundSellerPosition,
    selectIsAnyFundFormInEditMode,
    selectSelectedFund
} from "../../store/fund/fund.selectors";
import {FundForms} from "../../models/fund-forms";
import {
    selectCalculatedFundValuation,
    selectSelectedFundValuation,
    selectUndrawnGpQualityMissing,
    selectUndrawnMissingFields,
    selectUndrawnValuation
} from "../../store/fund-valuation/fund-valuation.selectors";
import {FundValuationScenario} from "../../models/fund-valuation-scenario";
import {EMPTY_FUND_VALUATION, FundValuation} from "../../models/fund-valuation";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {combineLatest, first, map, Subscription} from "rxjs";
import {FundFieldKey, OutstandingItem} from "../../models/fund-field-key.enum";
import {CodeTableEnum} from "../../../shared/model/code";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {selectSelectedFundReport} from "../../store/fund-report/fund-report.selectors";
import {TraceableMoney, TraceablePercent} from "../../../shared/model/traceable";
import Big from "big.js";

@Component({
    selector: "valumize-fund-undrawn-valuation",
    templateUrl: "./fund-undrawn-valuation.component.html",
    styleUrls: ["./fund-undrawn-valuation.component.scss"],
    standalone: false
})
export class FundUndrawnValuationComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly renderer = inject(Renderer2);
    private readonly snackBar = inject(MatSnackBar);
    private readonly injector = inject(Injector);

    subscription?: Subscription;

    missingFields$ = this.store.select(selectUndrawnMissingFields);
    undrawnValuation$ = this.store.select(selectUndrawnValuation);
    selectedFundValuation$ = this.store.select(selectSelectedFundValuation);
    calculatedFundValuation$ = this.store.select(selectCalculatedFundValuation);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);
    fundReport$ = this.store.select(selectSelectedFundReport);
    gpQualityMissing$ = this.store.select(selectUndrawnGpQualityMissing);

    readonly gpQualityMissingMessage = "Set the fund's GP quality first — it selects the undrawn-multiple column.";
    fundSellerPosition$ = this.store.select(selectFundSellerPosition);
    selectedFund$ = this.store.select(selectSelectedFund);

    /** WI 1188: where each outstanding field is edited — section to scroll to, field to highlight, edit action. */
    private readonly fieldNav: Record<FundFieldKey, { sectionId: string; anchorId: string; edit: () => Action }> = {
        [FundFieldKey.Currency]:          {sectionId: "fund-details",         anchorId: "field-currency",           edit: () => FundDetailActions.edit()},
        [FundFieldKey.VintageYear]:       {sectionId: "fund-details",         anchorId: "field-vintageYear",        edit: () => FundDetailActions.edit()},
        [FundFieldKey.EndYear]:           {sectionId: "fund-details",         anchorId: "field-endYear",            edit: () => FundDetailActions.edit()},
        [FundFieldKey.FundSize]:          {sectionId: "fund-details",         anchorId: "field-size",               edit: () => FundDetailActions.edit()},
        [FundFieldKey.ManagementFee]:     {sectionId: "fund-details",         anchorId: "field-managementFee",      edit: () => FundDetailActions.edit()},
        [FundFieldKey.Carry]:             {sectionId: "fund-details",         anchorId: "field-carry",              edit: () => FundDetailActions.edit()},
        [FundFieldKey.Hurdle]:            {sectionId: "fund-details",         anchorId: "field-hurdleRate",         edit: () => FundDetailActions.edit()},
        [FundFieldKey.SecManagementFee]:  {sectionId: "fund-details",         anchorId: "field-secManagementFee",   edit: () => FundDetailActions.edit()},
        [FundFieldKey.SecCarry]:          {sectionId: "fund-details",         anchorId: "field-secCarry",           edit: () => FundDetailActions.edit()},
        [FundFieldKey.SecHurdle]:         {sectionId: "fund-details",         anchorId: "field-secHurdleRate",      edit: () => FundDetailActions.edit()},
        [FundFieldKey.TotalDrawdown]:     {sectionId: "fund-report-details",  anchorId: "field-totalDrawDowns",     edit: () => FundReportActions.edit()},
        [FundFieldKey.TotalDistribution]: {sectionId: "fund-report-details",  anchorId: "field-totalDistributions", edit: () => FundReportActions.edit()},
        [FundFieldKey.TotalReportedNav]:  {sectionId: "fund-report-details",  anchorId: "field-totalReportedNav",   edit: () => FundReportActions.edit()}
    };

    persistedScenarios: FundValuationScenario[] = [];
    persistedFundValuation: FundValuation = EMPTY_FUND_VALUATION;
    calculatedScenarios: FundValuationScenario[] = [];
    calculatedFundValuation?: FundValuation;

    isEditable = false;
    codeTableScenarioType = CodeTableEnum.SHARED_SCENARIOTYPE;

    constructor() {
    }

    undrawnValuationForm = this.formBuilder.group({
        percentageToBeDrawnCalc: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m()]}),
        unfundedDistributionSpeedCalc: this.formBuilder.control<number | null>(null, {
            nonNullable: true,
            validators: [Validators.required, Validators.min(1), Validators.max(500)]
        }),
        unfundedDrawdownSpeedCalc: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, Validators.min(1), Validators.max(500)]}),
        undrawnMultipleLOW: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m()]}),
        undrawnMultipleBASE: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m()]}),
        undrawnMultipleHIGH: this.formBuilder.control<number | null>(null, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m()]}),
    });

    nextFocus?: string;
    controlNamesForFocus = ["percentageToBeDrawnCalc", "unfundedDistributionSpeedCalc", "unfundedDrawdownSpeedCalc",
        "undrawnMultipleLOW", "undrawnMultipleBASE", "undrawnMultipleHIGH"];

    speedOptions: number[] = [1, 5, 10, 15, 25, 40, 60, 75, 90, 95, 100, 105, 110, 125, 150, 200, 250, 300, 400, 450, 500];

    ngOnInit() {
        this.subscription = this.store.select(selectUndrawnValuation).pipe(map((undrawnValuation) => {
            this.isEditable = undrawnValuation.undrawnValuationIsEditable;

            this.persistedFundValuation = undrawnValuation.persistedFundValuation;
            this.persistedScenarios = undrawnValuation.persistedScenarios;
            this.calculatedFundValuation = undrawnValuation.calculatedFundValuation;
            this.calculatedScenarios = undrawnValuation.calculatedScenarios;

            const lowScenarioCalc = this.calculatedScenarios.find(s => s.scenario.code === "LOW");
            const baseScenarioCalc = this.calculatedScenarios.find(s => s.scenario.code === "BASE");
            const highScenarioCalc = this.calculatedScenarios.find(s => s.scenario.code === "HIGH");

            this.undrawnValuationForm.patchValue({
                percentageToBeDrawnCalc: DecimalFormatPipe.transformFractionToPercent(undrawnValuation.calculatedFundValuation.percentageToBeDrawn.fraction ?? undefined),
                unfundedDistributionSpeedCalc: DecimalFormatPipe
                    .transformFractionToPercent(undrawnValuation.calculatedFundValuation.unfundedDistributionSpeed.fraction ?? undefined),
                unfundedDrawdownSpeedCalc: DecimalFormatPipe.transformFractionToPercent(undrawnValuation.calculatedFundValuation.unfundedDrawdownSpeed.fraction ?? undefined),
                undrawnMultipleLOW: lowScenarioCalc?.undrawnMultiple.factor,
                undrawnMultipleBASE: baseScenarioCalc?.undrawnMultiple.factor,
                undrawnMultipleHIGH: highScenarioCalc?.undrawnMultiple.factor
            });

            if (this.isEditable) {
                // Use a setTimeout to refocus on the next Javascript event loop tick,
                // giving Angular a chance to update the DOM first
                setTimeout(() => {
                    if (this.nextFocus) {
                        this.renderer.selectRootElement("#" + this.nextFocus).focus();
                    }
                }, 0);
                this.undrawnValuationForm.enable();
            } else {
                this.undrawnValuationForm.disable();
            }
        })).subscribe();
    }

    /** WI 1188: FundForms edit flag for the card that owns each fieldNav section. */
    private readonly ownerForm = new Map<string, keyof FundForms>([
        ["fund-details", "fundDetailIsEditable"],
        ["fund-report-details", "fundReportDetailsIsEditable"]
    ]);

    /**
     * WI 1188: navigate from an outstanding-item link to where the field is edited — scroll to the
     * owning card, open its edit mode (unless it is already open), then focus the field. When the field
     * can't be reached we explain why instead of scrolling to a dead end: the fund is archived, a
     * different card has unsaved edits, or (for report fields) no fund report exists yet.
     */
    goToField(item: OutstandingItem) {
        const nav = this.fieldNav[item.key];
        const owner = this.ownerForm.get(nav.sectionId)!;

        combineLatest([
            this.store.select(selectFundIsArchived),
            this.store.select(selectFundForms),
            this.store.select(selectSelectedFundReport)
        ]).pipe(first())
            .subscribe(([isArchived, forms, fundReport]) => {
                if (isArchived.value) {
                    this.snackBar.open("This fund is archived and can't be edited.", "OK", {duration: 6000});
                    return;
                }
                // Report fields live in the Fund Report card, which isn't shown until a report exists.
                if (nav.sectionId === "fund-report-details" && fundReport?.fundReport?.data?.id == null) {
                    this.snackBar.open(`Create a fund report first, then fill in ${item.label}.`, "OK", {duration: 6000});
                    return;
                }
                // Don't clobber another card's unsaved edit; the field's own card being open is fine.
                const otherCardEditing = (Object.keys(forms) as (keyof FundForms)[]).some((key) => forms[key] && key !== owner);
                if (otherCardEditing) {
                    this.snackBar.open("Finish editing the current section before jumping to another field.", "OK", {duration: 6000});
                    return;
                }

                document.getElementById(nav.sectionId)?.scrollIntoView({behavior: "smooth"});
                if (!forms[owner]) {
                    this.store.dispatch(nav.edit());
                }

                // Focus the control once the just-opened editor has rendered its input.
                afterNextRender(() => {
                    const focusable = document.getElementById(nav.anchorId)
                        ?.querySelector("input, select, mat-select") as HTMLElement | null;
                    focusable?.focus();
                }, {injector: this.injector});
            });
    }

    save() {
        if (this.undrawnValuationForm.valid) {
            this.store.select(selectCalculatedFundValuation).pipe(
                first(val => val?.status === "LOADED"),
                map((calculatedFundValuation) => {
                        const scenarios = this.calculatedScenarios;
                        if (calculatedFundValuation && scenarios) {
                            this.store.dispatch(FundValuationActions.save({fundValuation: calculatedFundValuation.data, scenarios}))
                        }
                    }
                )).subscribe();
        }
    }

    calc(focus: string) {
        const currentIndex = this.controlNamesForFocus.findIndex(control => control === focus);
        this.nextFocus = this.controlNamesForFocus[currentIndex + 1] || this.controlNamesForFocus[0];
        if (this.undrawnValuationForm.valid) {
            const updatedFundValuation = this.fundValuationForCalc();
            const updatedScenarios = this.scenarioForCalc(this.persistedScenarios, this.undrawnValuationForm);
            this.store.dispatch(UndrawnValuationActions.calc({fundValuation: updatedFundValuation, scenarios: updatedScenarios}));
        }
    }

    applyUnfundedParams() {
        const updatedFundValuation = this.fundValuationForCalc();
        const updatedScenarios = this.scenarioForCalc(this.persistedScenarios, this.undrawnValuationForm);
        this.store.dispatch(UndrawnValuationActions.applyUnfundedParams({fundValuation: updatedFundValuation, scenarios: updatedScenarios}));
    }

    editMode = () => this.store.dispatch(UndrawnValuationActions.edit());

    cancel = () => this.store.dispatch(UndrawnValuationActions.cancel());

    fundValuationForCalc(): FundValuation {
        const percentageToBeDrawn =
            DecimalFormatPipe.transformPercentToFraction(this.undrawnValuationForm.controls.percentageToBeDrawnCalc.value)
            ?? this.persistedFundValuation.percentageToBeDrawn.fraction;

        const unfundedDistributionSpeed =
            DecimalFormatPipe.transformPercentToFraction(this.undrawnValuationForm.controls.unfundedDistributionSpeedCalc.value)
            ?? this.persistedFundValuation.unfundedDistributionSpeed.fraction;

        const unfundedDrawdownSpeed =
            DecimalFormatPipe.transformPercentToFraction(this.undrawnValuationForm.controls.unfundedDrawdownSpeedCalc.value)
            ?? this.persistedFundValuation.unfundedDrawdownSpeed.fraction;

        return {
            ...this.persistedFundValuation,
            percentageToBeDrawn: {
                ...this.persistedFundValuation.percentageToBeDrawn,
                fraction: percentageToBeDrawn
            },
            unfundedDistributionSpeed: {
                ...this.persistedFundValuation.unfundedDistributionSpeed,
                fraction: unfundedDistributionSpeed
            },
            unfundedDrawdownSpeed: {
                ...this.persistedFundValuation.unfundedDrawdownSpeed,
                fraction: unfundedDrawdownSpeed
            }
        };
    }

    scenarioForCalc(
        oldScenarios: FundValuationScenario[],
        form: FormGroup<{
            percentageToBeDrawnCalc: FormControl<number | null>;
            unfundedDistributionSpeedCalc: FormControl<number | null>;
            unfundedDrawdownSpeedCalc: FormControl<number | null>;
            undrawnMultipleLOW: FormControl<number | null>;
            undrawnMultipleBASE: FormControl<number | null>;
            undrawnMultipleHIGH: FormControl<number | null>;
        }>
    ): FundValuationScenario[] {
        const lowScenario = oldScenarios.find(s => s.scenario.code === "LOW");
        const baseScenario = oldScenarios.find(s => s.scenario.code === "BASE");
        const highScenario = oldScenarios.find(s => s.scenario.code === "HIGH");
        const weightedScenario = oldScenarios.find(s => s.scenario.code === "WEIGHTED");

        const updatedFormValues = form.getRawValue();
        const updatedScenarios: FundValuationScenario[] = [];

        if (lowScenario) {
            const updatedLowScenario = this.createScenario(lowScenario, updatedFormValues.undrawnMultipleLOW ?? undefined);
            updatedScenarios.push(updatedLowScenario);
        }
        if (baseScenario) {
            const updatedBaseScenario = this.createScenario(baseScenario, updatedFormValues.undrawnMultipleBASE ?? undefined);
            updatedScenarios.push(updatedBaseScenario);
        }
        if (highScenario) {
            const updatedHighScenario = this.createScenario(highScenario, updatedFormValues.undrawnMultipleHIGH ?? undefined);
            updatedScenarios.push(updatedHighScenario);
        }
        if (weightedScenario) {
            updatedScenarios.push(weightedScenario);
        }
        return updatedScenarios;
    }

    private createScenario(scenario: FundValuationScenario, undrawnMultiple?: number): FundValuationScenario {
        return {
            ...scenario,
            undrawnMultiple: {
                ...scenario.undrawnMultiple,
                factor: undrawnMultiple
            }
        };
    }

    calcTotalUndrawnWithPercentage(totalUndrawn: TraceableMoney, percentageToBeDrawn: TraceablePercent): TraceableMoney {
        const undrawnAmountBig = new Big(percentageToBeDrawn.fraction ?? 0);
        const percentageBig = new Big(totalUndrawn.amount ?? 0);

        const result = undrawnAmountBig.times(percentageBig).toNumber(); // Big instance

        return {
            amount: result
        };
    }

    calcSellerAmountToBeDrawn(totalUndrawn?: TraceableMoney, percentageToBeDrawn?: TraceablePercent, sellerStake?: TraceablePercent): TraceableMoney {
        if (totalUndrawn?.amount == null || percentageToBeDrawn?.fraction == null || sellerStake?.fraction == null) {
            return {amount: undefined};
        }
        const result = new Big(totalUndrawn.amount)
            .times(percentageToBeDrawn.fraction)
            .times(sellerStake.fraction)
            .toNumber();
        return {amount: result};
    }

    calcCurrentDDLevel(commitment?: TraceableMoney, calculatedUndrawn?: TraceableMoney): TraceablePercent {
        if (commitment?.amount == null || calculatedUndrawn?.amount == null || commitment.amount === 0) {
            return {fraction: undefined};
        }
        const result = new Big(1).minus(new Big(calculatedUndrawn.amount).div(commitment.amount)).toNumber();
        return {fraction: result};
    }

    calcImpliedFinalDrawdownPct(sellerAmountToBeDrawn: TraceableMoney, drawdown?: TraceablePercent, sellerCommitment?: TraceableMoney): TraceablePercent {
        if (sellerAmountToBeDrawn.amount == null || drawdown?.fraction == null || !sellerCommitment?.amount) {
            return {fraction: undefined};
        }
        const result = new Big(drawdown.fraction)
            .plus(new Big(sellerAmountToBeDrawn.amount).div(sellerCommitment.amount))
            .toNumber();
        return {fraction: result};
    }

    getDistributionSpeedIndex(): number {
        const currentValue = this.undrawnValuationForm.controls.unfundedDistributionSpeedCalc.value ?? 100;
        const index = this.speedOptions.indexOf(currentValue);
        return index !== -1 ? index : this.getNearestIndex(currentValue);
    }

    getDrawdownSpeedIndex(): number {
        const currentValue = this.undrawnValuationForm.controls.unfundedDrawdownSpeedCalc.value ?? 100;
        const index = this.speedOptions.indexOf(currentValue);
        return index !== -1 ? index : this.getNearestIndex(currentValue);
    }

    getNearestIndex(value: number): number {
        return this.speedOptions.reduce((bestIdx, curr, idx) => {
            return Math.abs(curr - value) < Math.abs(this.speedOptions[bestIdx] - value) ? idx : bestIdx;
        }, 0);
    }

    onDistributionSpeedIndexChange(newIndex: number): void {
        const newValue = this.speedOptions[newIndex];
        this.undrawnValuationForm.controls.unfundedDistributionSpeedCalc.setValue(newValue);
        this.calc("unfundedDistributionSpeedCalc");
    }

    onDrawdownSpeedIndexChange(newIndex: number): void {
        const newValue = this.speedOptions[newIndex];
        this.undrawnValuationForm.controls.unfundedDrawdownSpeedCalc.setValue(newValue);
        this.calc("unfundedDrawdownSpeedCalc");
    }

    styleRowImportant(fieldName: string): string {
        return fieldName === "WEIGHTED" ? "row-important row-top-border" : "";
    }

    showRequiredAsterisk(fieldName: string): string {
        return fieldName !== "WEIGHTED" ? "required-asterisk" : "";
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
